/**
 * Timestamp-based Promotion Countdown Custom Element
 * 基于UTC时间戳的活动倒计时自定义元素
 * 使用服务器端计算的UTC时间戳确保跨时区一致性
 */

class PromotionCountdown extends HTMLElement {
  constructor() {
    super();
    this.isInitialized = false;
    this.interval = null;
    this.isCompleted = false;
    this.startTime = Date.now();
  }

  connectedCallback() {
    if (!this.isInitialized) {
      this.init();
    }
  }

  disconnectedCallback() {
    this.clearAllIntervals();
  }

  init() {
    // 获取倒计时元素
    this.timerElement = this.querySelector(".countdown-timer");
    if (!this.timerElement) {
      console.warn("Promotion Countdown: Timer element not found");
      return;
    }

    // 从data属性获取时间戳（秒级）
    this.targetTimestamp = parseInt(this.timerElement.dataset.targetTimestamp) * 1000; // 转换为毫秒
    this.currentTimestamp = parseInt(this.timerElement.dataset.currentTimestamp) * 1000; // 转换为毫秒
    this.hideOnComplete = this.timerElement.dataset.hideOnComplete === "true";
    this.redirectUrl = this.timerElement.dataset.redirectUrl;
    this.completionMessage = this.timerElement.dataset.completionMessage || "Offer has ended!";
    this.debugInfo = this.timerElement.dataset.debugInfo;

    // DOM元素引用
    this.daysElement = this.querySelector("[data-days]");
    this.hoursElement = this.querySelector("[data-hours]");
    this.minutesElement = this.querySelector("[data-minutes]");
    this.secondsElement = this.querySelector("[data-seconds]");
    this.displayElement = this.querySelector(".countdown-display");

    // 计算服务器时间和本地时间的偏移量
    this.timeOffset = this.currentTimestamp - this.startTime;

    if (!this.targetTimestamp || isNaN(this.targetTimestamp)) {
      console.warn("Promotion Countdown: Invalid target timestamp");
      return;
    }

    // 调试信息
    if (this.debugInfo && window.COUNTDOWN_DEBUG) {
    }

    // 立即检查是否已过期
    const currentTime = this.getCurrentTime();
    if (this.targetTimestamp <= currentTime) {
      this.handleCompletion();
      return;
    }

    // 显示组件（加载完成后）
    this.style.display = "block";
    this.isInitialized = true;

    // 开始倒计时
    this.startCountdown();

    // 立即更新一次显示
    this.updateDisplay();

    // 初始化优惠码复制功能
    this.initCouponCopy();
  }

  getCurrentTime() {
    // 使用服务器时间偏移量计算当前准确时间
    return Date.now() + this.timeOffset;
  }

  startCountdown() {
    this.interval = setInterval(() => {
      this.updateDisplay();
    }, 1000);
  }

  updateDisplay() {
    const currentTime = this.getCurrentTime();
    const timeDiff = this.targetTimestamp - currentTime;

    if (timeDiff <= 0) {
      this.handleCompletion();
      return;
    }

    // 计算各时间单位
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    // 更新显示
    if (this.daysElement) this.daysElement.textContent = this.formatNumber(days);
    if (this.hoursElement) this.hoursElement.textContent = this.formatNumber(hours);
    if (this.minutesElement) this.minutesElement.textContent = this.formatNumber(minutes);
    if (this.secondsElement) this.secondsElement.textContent = this.formatNumber(seconds);
  }

  formatNumber(num) {
    return num.toString().padStart(2, "0");
  }

  handleCompletion() {
    if (this.isCompleted) return;

    this.isCompleted = true;

    // 停止所有定时器
    this.clearAllIntervals();

    // 直接隐藏整个组件，不显示任何过期提示
    this.style.display = "none";

    // 如果设置了重定向URL，则进行重定向
    if (this.redirectUrl) {
      setTimeout(() => {
        window.location.href = this.redirectUrl;
      }, 1000);
    }
  }

  clearAllIntervals() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  initCouponCopy() {
    const copyButton = this.querySelector(".coupon-copy-btn:not([data-initialized])");
    if (copyButton) {
      copyButton.setAttribute("data-initialized", "true");
      copyButton.addEventListener("click", (e) => {
        e.preventDefault();
        const couponCode = this.querySelector(".coupon-code").textContent;
        this.copyCode(couponCode, copyButton);
      });
    }
  }

  copyCode(code, button) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          this.showCopySuccess(button);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          this.fallbackCopy(code, button);
        });
    } else {
      this.fallbackCopy(code, button);
    }
  }

  fallbackCopy(code, button) {
    const textArea = document.createElement("textarea");
    textArea.value = code;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        this.showCopySuccess(button);
      }
    } catch (err) {
      console.error("Fallback copy failed: ", err);
    }

    document.body.removeChild(textArea);
  }

  showCopySuccess(button) {
    const originalText = button.dataset.copyText;

    button.textContent = button.dataset.copySuccessText || "Copied!";
    button.classList.add("copied");
    this.showToast();
    // 添加成功动画
    button.style.transform = "scale(0.95)";
    setTimeout(() => {
      button.style.transform = "scale(1)";
    }, 100);

    // 显示 toast 提示

    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove("copied");
    }, 2000);
  }

  showToast() {
    const toastContainer = document.querySelector(".toast-container");
    if (toastContainer) {
      // 移除可能存在的动画类
      toastContainer.classList.remove("toast-show", "toast-hide");

      // 显示 toast
      toastContainer.classList.add("toast-show");

      // 3秒后隐藏 toast
      setTimeout(() => {
        toastContainer.classList.remove("toast-show");
        toastContainer.classList.add("toast-hide");

        // 动画结束后完全隐藏
        setTimeout(() => {
          toastContainer.classList.remove("toast-hide");
        }, 300);
      }, 1000);
    }
  }

  // 获取剩余时间（用于外部查询）
  getRemainingTime() {
    const currentTime = this.getCurrentTime();
    const timeDiff = this.targetTimestamp - currentTime;

    if (timeDiff <= 0) {
      return {
        total: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: true,
      };
    }

    return {
      total: timeDiff,
      days: Math.floor(timeDiff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((timeDiff % (1000 * 60)) / 1000),
      expired: false,
    };
  }
}

// 注册自定义元素
if (!customElements.get("promotion-countdown")) {
  customElements.define("promotion-countdown", PromotionCountdown);
}

// 倒计时管理器
class PromotionCountdownManager {
  static init() {
    // 查找所有倒计时元素并初始化
    const countdownElements = document.querySelectorAll("promotion-countdown");
    countdownElements.forEach((element) => {
      if (!element.isInitialized) {
        element.init();
      }
    });
  }

  // 获取所有活动倒计时的状态
  static getAllCountdownStatus() {
    const countdowns = document.querySelectorAll("promotion-countdown");
    const status = [];

    countdowns.forEach((element) => {
      if (element.isInitialized) {
        const remaining = element.getRemainingTime();

        status.push({
          element: element,
          blockId: element.dataset.blockId,
          remaining: remaining,
          targetTimestamp: element.targetTimestamp,
          isCompleted: element.isCompleted,
        });
      }
    });

    return status;
  }
}

// 全局复制函数（向后兼容）
window.copyCode = function (code, blockId) {
  const countdownElement = document.querySelector(`#promotion-countdown-${blockId}`);
  if (countdownElement) {
    const button = countdownElement.querySelector(".coupon-copy-btn");
    if (button) {
      countdownElement.copyCode(code, button);
    }
  }
};

// 全局调试开关
window.COUNTDOWN_DEBUG = false;

// DOM加载完成后初始化
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    PromotionCountdownManager.init();
  });
} else {
  PromotionCountdownManager.init();
}

// Shopify主题编辑器支持
if (typeof window.Shopify !== "undefined" && window.Shopify.designMode) {
  document.addEventListener("shopify:block:load", function (event) {
    const countdownElements = event.target.querySelectorAll("promotion-countdown");
    countdownElements.forEach((element) => {
      if (element.clearAllIntervals) {
        element.clearAllIntervals();
      }
      element.init();
    });
  });

  document.addEventListener("shopify:block:unload", function (event) {
    const countdownElements = event.target.querySelectorAll("promotion-countdown");
    countdownElements.forEach((element) => {
      if (element.clearAllIntervals) {
        element.clearAllIntervals();
      }
    });
  });
}

// 导出类以供其他脚本使用
window.PromotionCountdown = PromotionCountdown;
window.PromotionCountdownManager = PromotionCountdownManager;
